/**
 * src/pages/WhatIsConsciousness/sections/BrainExplorer.tsx
 * Interactive 2D brain explorer mapping key neuroanatomical structures,
 * their functional roles, and evidence-labelled relations to conscious experience.
 */

import React, { useState } from "react";
import { Brain, Layers, Activity, AlertCircle } from "lucide-react";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import { BRAIN_REGIONS } from "../consciousnessData";
import "@/components/cosmic/cosmic.css";
export function BrainExplorer() {
  const [selectedId, setSelectedId] = useState<string>("prefrontal");
  const activeRegion = BRAIN_REGIONS.find(r => r.id === selectedId) || BRAIN_REGIONS[0];
  return <section className="w-full max-w-full min-w-0" aria-labelledby="brain-explorer-heading">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="size-1.5 rounded-[1px] bg-primary rotate-45 shadow-[0_0_8px_1px_var(--glow-primary)] shrink-0" aria-hidden="true" />
            <span className="ql-eyebrow">Neuroanatomy & Experience</span>
          </div>
          <h2 id="brain-explorer-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            The Brain & Neural Correlates
          </h2>
        </div>
        <p className="font-sans text-xs sm:text-sm text-muted-foreground max-w-md">
          Roughly 86 billion neurons communicate via trillions of synaptic junctions.
          Yet no single cortical "seat" produces subjective awareness alone.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start min-w-0">
        {/* Interactive Brain Map (SVG) */}
        <div className="lg:col-span-6 flex flex-col gap-4 min-w-0">
          <div className="ql-glass p-4 sm:p-6 w-full max-w-full min-w-0 flex flex-col items-center">
            <div className="w-full flex items-center justify-between mb-4 text-xs font-mono text-muted-foreground border-b border-border/30 pb-2">
              <span className="flex items-center gap-1.5 text-primary">
                <Brain className="size-3.5" />
                <span>Sagittal Hemispheric Topology</span>
              </span>
              <span>Select anatomical zone</span>
            </div>

            {/* Brain Outline SVG */}
            <div className="relative w-full max-w-full aspect-[4/3] flex items-center justify-center">
              <svg viewBox="0 0 400 300" className="w-full max-w-full h-auto max-h-full block" aria-label="Interactive sagittal diagram of the human brain with clickable regions">
                {/* Background cerebral cortex silhouette */}
                <path d="M 120 70 C 180 30, 290 35, 330 90 C 365 140, 350 200, 310 220 C 270 240, 240 220, 230 200 C 210 205, 170 205, 150 185 C 110 180, 80 140, 90 100 Z" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-border/60" />

                {/* Prefrontal Cortex (Frontal lobe - left anterior) */}
                <path d="M 90 100 C 80 140, 110 180, 150 185 C 155 150, 160 120, 160 90 C 145 80, 130 75, 120 70 Z" fill={selectedId === "prefrontal" ? "var(--accent)" : "color-mix(in oklab, var(--card) 60%, transparent)"} stroke={selectedId === "prefrontal" ? "var(--primary)" : "var(--border)"} strokeWidth={selectedId === "prefrontal" ? "2.5" : "1.2"} className="cursor-pointer transition-all hover:opacity-90" onClick={() => setSelectedId("prefrontal")} onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setSelectedId("prefrontal");
              }} tabIndex={0} role="button" aria-label="Prefrontal Cortex region" aria-pressed={selectedId === "prefrontal"} />

                {/* Visual Cortex (Occipital lobe - posterior right) */}
                <path d="M 330 90 C 365 140, 350 200, 310 220 C 300 200, 290 170, 280 140 C 300 110, 315 100, 330 90 Z" fill={selectedId === "visual" ? "var(--accent)" : "color-mix(in oklab, var(--card) 60%, transparent)"} stroke={selectedId === "visual" ? "var(--primary)" : "var(--border)"} strokeWidth={selectedId === "visual" ? "2.5" : "1.2"} className="cursor-pointer transition-all hover:opacity-90" onClick={() => setSelectedId("visual")} onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setSelectedId("visual");
              }} tabIndex={0} role="button" aria-label="Visual Cortex region" aria-pressed={selectedId === "visual"} />

                {/* Central Thalamus (deep core oval) */}
                <ellipse cx="200" cy="145" rx="30" ry="20" fill={selectedId === "thalamus" ? "var(--accent)" : "color-mix(in oklab, var(--card) 60%, transparent)"} stroke={selectedId === "thalamus" ? "var(--primary)" : "var(--border)"} strokeWidth={selectedId === "thalamus" ? "2.5" : "1.2"} className="cursor-pointer transition-all hover:opacity-90" onClick={() => setSelectedId("thalamus")} onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setSelectedId("thalamus");
              }} tabIndex={0} role="button" aria-label="Thalamus central core" aria-pressed={selectedId === "thalamus"} />

                {/* Brainstem (caudal stalk exiting downwards) */}
                <path d="M 190 170 L 215 170 L 210 260 L 185 260 Z" fill={selectedId === "brainstem" ? "var(--accent)" : "color-mix(in oklab, var(--card) 60%, transparent)"} stroke={selectedId === "brainstem" ? "var(--primary)" : "var(--border)"} strokeWidth={selectedId === "brainstem" ? "2.5" : "1.2"} className="cursor-pointer transition-all hover:opacity-90" onClick={() => setSelectedId("brainstem")} onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setSelectedId("brainstem");
              }} tabIndex={0} role="button" aria-label="Brainstem region" aria-pressed={selectedId === "brainstem"} />

                {/* Hippocampus (medial temporal arch) */}
                <path d="M 175 165 C 190 175, 230 175, 240 160 C 235 150, 200 155, 175 165 Z" fill={selectedId === "hippocampus" ? "var(--accent)" : "color-mix(in oklab, var(--card) 60%, transparent)"} stroke={selectedId === "hippocampus" ? "var(--primary)" : "var(--border)"} strokeWidth={selectedId === "hippocampus" ? "2.5" : "1.2"} className="cursor-pointer transition-all hover:opacity-90" onClick={() => setSelectedId("hippocampus")} onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setSelectedId("hippocampus");
              }} tabIndex={0} role="button" aria-label="Hippocampus region" aria-pressed={selectedId === "hippocampus"} />

                {/* Cerebellum (posterior caudal cauliflower shape) */}
                <path d="M 230 200 C 260 190, 290 205, 280 240 C 260 255, 230 250, 220 220 Z" fill={selectedId === "cerebellum" ? "var(--accent)" : "color-mix(in oklab, var(--card) 60%, transparent)"} stroke={selectedId === "cerebellum" ? "var(--primary)" : "var(--border)"} strokeWidth={selectedId === "cerebellum" ? "2.5" : "1.2"} className="cursor-pointer transition-all hover:opacity-90" onClick={() => setSelectedId("cerebellum")} onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") setSelectedId("cerebellum");
              }} tabIndex={0} role="button" aria-label="Cerebellum region" aria-pressed={selectedId === "cerebellum"} />

                {/* Region Labels */}
                <text x="120" y="135" fill="currentColor" className="text-[10px] font-mono fill-foreground/80 pointer-events-none select-none">
                  PFC
                </text>
                <text x="305" y="160" fill="currentColor" className="text-[10px] font-mono fill-foreground/80 pointer-events-none select-none">
                  V1
                </text>
                <text x="185" y="148" fill="currentColor" className="text-[9px] font-mono fill-foreground/80 pointer-events-none select-none">
                  Thal
                </text>
                <text x="240" y="225" fill="currentColor" className="text-[9px] font-mono fill-foreground/80 pointer-events-none select-none">
                  Cereb
                </text>
                <text x="165" y="225" fill="currentColor" className="text-[9px] font-mono fill-foreground/80 pointer-events-none select-none">
                  Stem
                </text>
              </svg>
            </div>

            {/* Quick buttons list */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-4 pt-4 border-t border-border/30 w-full">
              {BRAIN_REGIONS.map(region => {
              const isActive = region.id === selectedId;
              return <button key={region.id} type="button" onClick={() => setSelectedId(region.id)} className={`ql-chip min-h-[44px] px-3.5 py-2 inline-flex items-center justify-center text-xs ${isActive ? "border-primary text-foreground" : ""}`} data-active={isActive ? "true" : "false"} aria-pressed={isActive}>
                    <span>{region.name.split(" ")[0]}</span>
                  </button>;
            })}
            </div>
          </div>
        </div>

        {/* Selected Region Detailed Telemetry Panel */}
        <div className="lg:col-span-6 flex flex-col gap-4 min-w-0">
          <div className="ql-glass p-6 sm:p-7 w-full max-w-full min-w-0 flex flex-col gap-5">
            <div className="flex flex-col gap-2 border-b border-border/30 pb-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <EvidenceBadge level={activeRegion.evidenceLevel} />
                <span className="font-mono text-xs text-muted-foreground">
                  Ref: {activeRegion.refs.map(r => <Footnote key={r} n={r} />)}
                </span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
                {activeRegion.name}
              </h3>
              <p className="font-mono text-xs text-primary">
                {activeRegion.subTitle}
              </p>
            </div>

            {/* General Function */}
            <div className="space-y-1.5">
              <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Layers className="size-3.5 text-primary" />
                <span>Primary Biological Function</span>
              </h4>
              <p className="font-sans text-sm text-foreground/90 leading-relaxed break-words">
                {activeRegion.roleDescription}
              </p>
            </div>

            {/* Relevance to Consciousness */}
            <div className="space-y-1.5 p-3.5 rounded-lg border border-primary/25 bg-primary/5">
              <h4 className="font-mono text-xs uppercase tracking-wider text-primary flex items-center gap-1.5 font-bold">
                <Activity className="size-3.5 text-primary" />
                <span>Relation to Subjective Experience</span>
              </h4>
              <p className="font-sans text-sm text-foreground leading-relaxed break-words">
                {activeRegion.consciousnessLink}
              </p>
            </div>

            {/* Empirical Observation */}
            <div className="space-y-1.5 pt-2 border-t border-border/30">
              <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Empirical Milestone
              </h4>
              <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed break-words italic">
                "{activeRegion.empiricalFact}"
              </p>
            </div>
          </div>

          {/* Scientific Nuance Callout */}
          <div className="p-4 rounded-lg border border-border/50 bg-card/40 flex items-start gap-3">
            <AlertCircle className="size-4 text-primary shrink-0 mt-0.5" />
            <p className="font-sans text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Correlation vs. Causation:</strong>{" "}
              Neural Correlates of Consciousness (NCCs) define minimal neuronal assemblies
              jointly sufficient for a specific conscious percept. Observing synchronized
              gamma oscillations or metabolic surges demonstrates correlation, not an ontological
              explanation of why subjective sensation arises.
            </p>
          </div>
        </div>
      </div>
    </section>;
}